import { Address, zeroAddress } from 'viem'
import { LifiQuoteResponse } from '@/features/swap/logic/types'
import { HOST } from '@/config/consts'

export const HYPEREVM_CHAIN_ID = 999

export interface LifiQuoteParams {
  fromToken: string
  toToken: string
  fromAmount: string
  fromAddress: Address
  /** Slippage in basis points (e.g., 50 = 0.5%) */
  slippageBps: number
}

/**
 * Converts token addresses for LiFi API compatibility.
 * - Native HYPE (0xEeee... or 0x0000...) should use the zero address for LiFi
 * - LiFi understands 0x0000... as native token
 */
export function lifiAddressOverride(address: Address): string {
  const nativeAddresses = [zeroAddress.toLowerCase(), '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee']

  if (nativeAddresses.includes(address.toLowerCase())) {
    return '0x0000000000000000000000000000000000000000'
  }

  return address
}

export class LifiFetchError extends Error {
  public status: number
  public details?: Record<string, unknown>

  constructor(message: string, status: number, details?: Record<string, unknown>) {
    super(message)
    this.name = 'LifiFetchError'
    this.status = status
    this.details = details
  }
}

function getLifiQuoteUrl(): string {
  if (typeof window !== 'undefined') {
    return '/api/lifi/quote'
  }

  return `${HOST}/api/lifi/quote`
}

export async function fetchLifiQuote(params: LifiQuoteParams): Promise<LifiQuoteResponse> {
  // Convert basis points to decimal for LiFi (50 bps -> 0.005)
  const slippageDecimal = params.slippageBps / 10000

  const response = await fetch(getLifiQuoteUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      fromChain: HYPEREVM_CHAIN_ID,
      toChain: HYPEREVM_CHAIN_ID,
      fromToken: params.fromToken,
      toToken: params.toToken,
      fromAmount: params.fromAmount,
      fromAddress: params.fromAddress,
      slippage: slippageDecimal.toString(),
      integrator: 'HypurrFi',
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to fetch quote' }))
    const message =
      typeof errorData.message === 'string'
        ? errorData.message
        : typeof errorData.error === 'string'
          ? errorData.error
          : `HTTP ${response.status}`

    throw new LifiFetchError(message, response.status, { response: errorData })
  }

  return response.json()
}
